import React, { useEffect, useState, useCallback, useMemo } from "react";
import get from "lodash/get";
import { useDispatch } from "react-redux";
import {
  Button,
  Card,
  Descriptions,
  Image,
  Space,
  Spin,
  Table,
  Tag,
  message,
  Typography,
  Modal,
  Input,
} from "antd";
import {
  CloudUploadOutlined,
  DatabaseOutlined,
  EyeOutlined,
  FileSearchOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";

import { overviewTable } from "../../constants/overview";
import {
  ORDER_PAYMENT_MODE,
  readableDateTime,
  TABLE_PAGINATION,
} from "../../utilities/helper";
import {
  EJECT_BATTERY,
  APPROVED_CABINET,
  RESTART_CABINET,
  CABINET_LOGS,
  TOGGLE_REMOTE_LOGGING,
  SYNC_CABINET_SLOTS,
  LOCK_CABINET_SLOT,
  OTA_CABINET,
  RESTART_KIOSK,
  CHECK_INVENTORY,
} from "../../constants/actionTypes";
import { postApi } from "../../redux/rest/restSlice";
import Can from "../../components/Can";
import PageHeader, { useGoBack } from "../../components/PageHeader";
import CopyText from "../../components/CopyText";
import DataTable from "../../components/DataTable";
import SecretValue from "../../components/SecretValue";
import TimeAgo from "../../components/TimeAgo";
import { PERMISSIONS } from "../../constants/permissions";

/* -------------------------------- Utilities -------------------------------- */

const safeJsonParse = (value) => {
  if (typeof value !== "string") return value || {};
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const flattenObject = (obj, parentKey = "", result = []) => {
  Object.entries(obj || {}).forEach(([key, value]) => {
    const newKey = parentKey ? `${parentKey}.${key}` : key;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      flattenObject(value, newKey, result);
    } else {
      result.push({
        key: newKey,
        value: String(value),
      });
    }
  });

  return result;
};

const vitalColumns = [
  { title: "Key", dataIndex: "key" },
  { title: "Value", dataIndex: "value" },
];

/* ------------------------------- Component ---------------------------------- */

// Shows the logo link; the image itself is only loaded when Preview is clicked
const LogoPreview = ({ url }) => {
  const [previewOpen, setPreviewOpen] = useState(false);

  return (
    <Space size="middle" wrap>
      <Typography.Link href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </Typography.Link>
      <Button size="small" icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>
        Preview
      </Button>
      {previewOpen && (
        <Image
          style={{ display: "none" }}
          src={url}
          alt="Customer support logo"
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
          }}
        />
      )}
    </Space>
  );
};

const OverviewScreen = () => {
  const dispatch = useDispatch();
  const { cabinetSn } = useParams();
  const goBack = useGoBack();
  const [modal, modalContextHolder] = Modal.useModal();

  // Ask before disruptive device actions
  const confirmAction = ({ title, content, okText, onOk }) =>
    modal.confirm({
      title,
      content,
      okText,
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk,
    });



  const [loading, setLoading] = useState(false);
  const [cabinetData, setCabinetData] = useState(null);
  const [isVitalModalOpen, setIsVitalModalOpen] = useState(false);

  /* ---------------------- NEW: Logs State ---------------------- */

  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);

  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [slots, setSlots] = useState("");

  const [isOtaModalOpen, setIsOtaModalOpen] = useState(false);
  const [otaVersion, setOtaVersion] = useState("");
  const [otaBuildUrl, setOtaBuildUrl] = useState("");

  /* ----------------------------- API Calls ---------------------------------- */

  const fetchCabinetDetails = useCallback(() => {
    if (!cabinetSn) return;

    setLoading(true);
    dispatch(
      postApi({
        endpoint: APPROVED_CABINET,
        payload: { filters: { cabinet_sn: cabinetSn } },
      }),
    )
      .then(unwrapResult)
      .then((result) => {
        if (result?.success) {
          setCabinetData(result?.data?.data?.[0] || null);
        } else {
          message.error("Failed to fetch cabinet details.");
        }
      })
      .catch(() => message.error("Failed to fetch cabinet details."))
      .finally(() => setLoading(false));
  }, [cabinetSn, dispatch]);

  /* ---------------------- NEW: Fetch Cabinet Logs ---------------------- */

  const fetchCabinetLogs = useCallback(
    (currOffset, currLimit) => {
      if (!cabinetSn) return;

      setLogsLoading(true);
      dispatch(
        postApi({
          endpoint: CABINET_LOGS,
          payload: {
            cabinet_sn: cabinetSn,
            offset: currOffset,
            limit: currLimit,
          },
        }),
      )
        .then(unwrapResult)
        .then((res) => {
          if (res?.success) {
            setLogs(res?.data || []);
            setLogsTotal(res?.total || 0);
          } else {
            message.error("Failed to fetch cabinet logs.");
          }
        })
        .catch(() => message.error("Failed to fetch cabinet logs."))
        .finally(() => setLogsLoading(false));
    },
    [cabinetSn, dispatch],
  );

  const handleEject = useCallback(
    (batteryId, sno) => {
      setLoading(true);
      dispatch(
        postApi({
          endpoint: EJECT_BATTERY,
          payload: { battery_id: batteryId, cabinet_id: sno },
        }),
      )
        .then(unwrapResult)
        .then((res) => {
          if (res?.success) {
            message.success("Battery ejected successfully.");
            fetchCabinetDetails();
          }
        })
        .catch(() => message.error("Failed to eject battery."))
        .finally(() => setLoading(false));
    },
    [dispatch, fetchCabinetDetails],
  );

  const handleSlotLock = useCallback(
    (position, isLocked) => {
      const action = isLocked ? "lock" : "unlock";
      setLoading(true);
      dispatch(
        postApi({
          endpoint: LOCK_CABINET_SLOT,
          payload: {
            cabinet_sn: get(cabinetData, "cabinet_sn"),
            position: Number(position),
            is_locked: isLocked,
          },
        }),
      )
        .then(unwrapResult)
        .then((res) => {
          if (res?.success) {
            message.success(
              res?.message || `Slot ${position} ${action}ed successfully.`,
            );
            fetchCabinetDetails();
          } else {
            message.error(`Failed to ${action} slot.`);
          }
        })
        .catch(() => message.error(`Failed to ${action} slot.`))
        .finally(() => setLoading(false));
    },
    [dispatch, cabinetData, fetchCabinetDetails],
  );

  const restartCabinet = useCallback(
    (sno) => {
      setLoading(true);
      dispatch(
        postApi({
          endpoint: RESTART_CABINET,
          payload: { cabinet_id: sno },
        }),
      )
        .then(unwrapResult)
        .then((res) => {
          if (res?.success) {
            message.success("Cabinet restarted successfully.");
          }
        })
        .catch(() => message.error("Failed to restart cabinet."))
        .finally(() => setLoading(false));
    },
    [dispatch],
  );

  const restartKiosk = useCallback(
    (sno) => {
      setLoading(true);
      dispatch(
        postApi({
          endpoint: RESTART_KIOSK,
          payload: { cabinet_id: sno },
        }),
      )
        .then(unwrapResult)
        .then((res) => {
          if (res?.success) {
            message.success("Kiosk restarted successfully.");
          }
        })
        .catch(() => message.error("Failed to restart kiosk."))
        .finally(() => setLoading(false));
    },
    [dispatch],
  );

  const forceCheckInventory = useCallback(
    (sno) => {
      setLoading(true);
      dispatch(
        postApi({
          endpoint: CHECK_INVENTORY,
          payload: { cabinet_id: sno },
        }),
      )
        .then(unwrapResult)
        .then((res) => {
          if (res?.success) {
            message.success("Message Triggered.");
          }
        })
        .catch(() => message.error("Failed to check inventory."))
        .finally(() => setLoading(false));
    },
    [dispatch],
  );

  const toggleRemoteLogging = useCallback(
    (sno) => {
      setLoading(true);
      dispatch(
        postApi({
          endpoint: TOGGLE_REMOTE_LOGGING,
          payload: { cabinet_id: sno },
        }),
      )
        .then(unwrapResult)
        .then((res) => {
          if (res?.success) {
            message.success("Remote Logging Enabled.");
          }
        })
        .catch(() => message.error("Failed to toggle remote logging."))
        .finally(() => setLoading(false));
    },
    [dispatch],
  );

  const handleSyncSlots = useCallback(() => {
    if (!slots || Number(slots) <= 0) {
      return message.error("Please enter valid number of slots");
    }

    setLoading(true);

    dispatch(
      postApi({
        endpoint: SYNC_CABINET_SLOTS,
        payload: {
          cabinet_sn: get(cabinetData, "cabinet_sn"),
          slots: Number(slots),
        },
      }),
    )
      .then(unwrapResult)
      .then((res) => {
        if (res?.success) {
          message.success("Cabinet slots synced successfully.");
          setIsSyncModalOpen(false);
          setSlots("");
        } else {
          message.error("Failed to sync cabinet slots.");
        }
      })
      .catch(() => message.error("Failed to sync cabinet slots."))
      .finally(() => setLoading(false));
  }, [dispatch, cabinetData, slots]);

  const handleOtaUpdate = useCallback(() => {
    if (!otaVersion || !otaBuildUrl) {
      return message.error("Please enter version and build URL");
    }

    setLoading(true);

    dispatch(
      postApi({
        endpoint: OTA_CABINET,
        payload: {
          cabinet_sn: get(cabinetData, "cabinet_sn"),
          version: otaVersion,
          build_url: otaBuildUrl,
        },
      }),
    )
      .then(unwrapResult)
      .then((res) => {
        if (res?.success) {
          message.success("OTA triggered successfully.");
          setIsOtaModalOpen(false);
          setOtaVersion("");
          setOtaBuildUrl("");
        } else {
          message.error("Failed to trigger OTA.");
        }
      })
      .catch(() => message.error("Failed to trigger OTA."))
      .finally(() => setLoading(false));
  }, [dispatch, cabinetData, otaVersion, otaBuildUrl]);

  const confirmOtaUpdate = () => {
    if (!otaVersion || !otaBuildUrl) {
      return message.error("Please enter version and build URL");
    }

    confirmAction({
      title: `Trigger OTA update to ${otaVersion}?`,
      content: `Cabinet ${get(cabinetData, "cabinet_sn")} will download and install this build.`,
      okText: "Trigger OTA",
      onOk: handleOtaUpdate,
    });
  };

  /* ----------------------------- Effects ------------------------------------ */

  useEffect(() => {
    fetchCabinetDetails();
  }, [fetchCabinetDetails]);

  useEffect(() => {
    fetchCabinetLogs(offset, limit);
  }, [fetchCabinetLogs, offset, limit]);

  /* ----------------------------- Derived Data -------------------------------- */

  const vitalInfoTableData = useMemo(() => {
    const rawVitalInfo = get(cabinetData, "vital_information");
    return flattenObject(safeJsonParse(rawVitalInfo));
  }, [cabinetData]);

  /* ---------------------- NEW: Logs Table Columns ---------------------- */

  const logsColumns = [
    {
      title: "ID",
      key: "serial_no",
      className: "col-index",
      width: 80,
      responsive: ["sm"],
      render: (_, __, index) => offset + index + 1,
    },
    {
      title: "Log Date",
      key: "created_at",
      dataIndex: "created_at",
      className: "col-date",
      render: (val) => <TimeAgo value={val} />,
    },
    {
      title: "Action",
      key: "action",
      dataIndex: "log_url",
      align: "right",
      className: "col-actions",
      fixed: "right",
      render: (url) => (
        <Button
          className="default-primary-btn"
          type="primary"
          onClick={() => window.open(url, "_blank")}
        >
          Download
        </Button>
      ),
    },
  ];

  const handleLogsPagination = (page, pageSize) => {
    const newOffset = (page - 1) * pageSize;
    setOffset(newOffset);
    setLimit(pageSize);
  };

  /* -------------------------------- Render ---------------------------------- */

  return (
    <Spin spinning={loading}>
      {modalContextHolder}
      <PageHeader onBack={goBack} title={get(cabinetData, "name", "--")} />

      <Card bordered={false} className="section-card">
        <Descriptions column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }} bordered>
          <Descriptions.Item label="Name">
            {get(cabinetData, "name", "--")}
          </Descriptions.Item>
          <Descriptions.Item label="Secret Code">
            <SecretValue value={get(cabinetData, "secret_code")} />
          </Descriptions.Item>
          <Descriptions.Item label="Sales Tax">
            {get(cabinetData, "sales_tax", "--")}
          </Descriptions.Item>
          <Descriptions.Item label="Hardware Id">
            {get(cabinetData, "cabinet_sn") ? (
              <CopyText value={get(cabinetData, "cabinet_sn")} />
            ) : (
              "--"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Kiosk Id">
            {get(cabinetData, "kiosk_id") ? (
              <CopyText value={get(cabinetData, "kiosk_id")} />
            ) : (
              "--"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Location Id">
            {get(cabinetData, "location_id", "--")}
          </Descriptions.Item>
          <Descriptions.Item label="Stripe Key">
            <SecretValue value={get(cabinetData, "stripe_key")} />
          </Descriptions.Item>
          <Descriptions.Item label="Address">
            {get(cabinetData, "address", "--")}
          </Descriptions.Item>
          <Descriptions.Item label="Advertisement Name">
            {get(cabinetData, "CabinetAdvertisement.advertisement_name", "--")}
          </Descriptions.Item>
          <Descriptions.Item label="Advertisement URL">
            {get(cabinetData, "CabinetAdvertisement.advertisement_url", "--")}
          </Descriptions.Item>
          <Descriptions.Item label="Customer Support Email">
            {get(cabinetData, "cs_email") ? (
              <CopyText value={get(cabinetData, "cs_email")} />
            ) : (
              "--"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Customer Support Phone Number">
            {get(cabinetData, "cs_phone_number") ? (
              <CopyText value={get(cabinetData, "cs_phone_number")} />
            ) : (
              "--"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Customer Support Logo">
            {get(cabinetData, "cs_logo") ? (
              <LogoPreview
                key={get(cabinetData, "cs_logo")}
                url={get(cabinetData, "cs_logo")}
              />
            ) : (
              "--"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={get(cabinetData, "is_working") ? "success" : "error"}>
              {get(cabinetData, "is_working") ? "Online" : "Offline"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Auto Restart">
            <Tag color={get(cabinetData, "auto_restart") ? "success" : "error"}>
              {get(cabinetData, "auto_restart") ? "Enabled" : "Disabled"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {get(cabinetData, "add_time")
              ? readableDateTime(get(cabinetData, "add_time"), "dd/mm/yyyy hh:mm")
              : "--"}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {get(cabinetData, "update_time")
              ? readableDateTime(
                  get(cabinetData, "update_time"),
                  "dd/mm/yyyy hh:mm",
                )
              : "--"}
          </Descriptions.Item>
          <Descriptions.Item label="Order Payment Mode">
            {ORDER_PAYMENT_MODE[get(cabinetData, "order_payment_mode")] || "--"}
          </Descriptions.Item>
          <Descriptions.Item label="Force Check Inventory">
            <Tag color={get(cabinetData, "force_check_inventory") ? "success" : "error"}>
              {get(cabinetData, "force_check_inventory") ? "Enabled" : "Disabled"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Pricing Name">
            {get(cabinetData, "CabinetPricing.pricing_name", "--")}
          </Descriptions.Item>
        </Descriptions>

        <div className="action-bar">
          {get(cabinetData, "kiosk_id") && cabinetData?.device_type === 4 && (
            <Can permissions={[PERMISSIONS.UPDATE_CABINET]}>
              <Button
                icon={<DatabaseOutlined />}
                onClick={() => forceCheckInventory(get(cabinetData, "cabinet_sn"))}
              >
                Check Inventory
              </Button>
            </Can>
          )}

          {get(cabinetData, "kiosk_id") && cabinetData?.device_type === 4 && (
            <Can permissions={[PERMISSIONS.UPDATE_CABINET]}>
              <Button
                danger
                icon={<ReloadOutlined />}
                onClick={() =>
                  confirmAction({
                    title: "Restart cabinet?",
                    content: `Cabinet ${get(cabinetData, "cabinet_sn")} will reboot and go offline for a short time.`,
                    okText: "Restart Cabinet",
                    onOk: () => restartCabinet(get(cabinetData, "cabinet_sn")),
                  })
                }
              >
                Restart Cabinet
              </Button>
            </Can>
          )}

          {get(cabinetData, "kiosk_id") && (
            <Can permissions={[PERMISSIONS.UPDATE_CABINET]}>
              <Button
                danger
                icon={<ReloadOutlined />}
                onClick={() =>
                  confirmAction({
                    title: "Restart kiosk?",
                    content: `The kiosk for cabinet ${get(cabinetData, "cabinet_sn")} will reboot and be unavailable for a short time.`,
                    okText: "Restart Kiosk",
                    onOk: () => restartKiosk(get(cabinetData, "cabinet_sn")),
                  })
                }
              >
                Restart Kiosk
              </Button>
            </Can>
          )}

          {get(cabinetData, "kiosk_id") && (
            <Can permissions={[PERMISSIONS.UPDATE_CABINET]}>
              <Button
                icon={<FileSearchOutlined />}
                onClick={() =>
                  toggleRemoteLogging(get(cabinetData, "cabinet_sn"))
                }
              >
                Remote Logging
              </Button>
            </Can>
          )}

          <Button
            icon={<InfoCircleOutlined />}
            onClick={() => setIsVitalModalOpen(true)}
          >
            View Vital Information
          </Button>
          <Can permissions={[PERMISSIONS.ONLY_FOR_SUPERADMIN]}>
            <Button
              icon={<SyncOutlined />}
              onClick={() => setIsSyncModalOpen(true)}
            >
              Sync Cabinet Slots
            </Button>
          </Can>
          <Can permissions={[PERMISSIONS.ONLY_FOR_SUPERADMIN]}>
            <Button
              danger
              icon={<CloudUploadOutlined />}
              onClick={() => setIsOtaModalOpen(true)}
            >
              OTA Update
            </Button>
          </Can>
        </div>
      </Card>

      <Card bordered={false} className="section-card mt-20">
        <DataTable
          key={Date.now()}
          scrollX={600}
          rowKey="id"
          dataSource={cabinetData?.CabinetBatteries ?? []}
          pagination={TABLE_PAGINATION}
          columns={overviewTable(
            cabinetData?.device_type,
            cabinetData?.cabinet_sn,
            handleEject,
            handleSlotLock,
          )}
          emptyProps={{ noun: "batteries" }}
        />
      </Card>

      {/* ---------------------- NEW: Logs Table ---------------------- */}

      <Card bordered={false} className="section-card mt-20" title="Cabinet Logs">
        <DataTable
          scrollX={500}
          rowKey={(record) => record.id || record.log_url || record.created_at}
          loading={logsLoading}
          columns={logsColumns}
          dataSource={logs}
          pagination={{
            ...TABLE_PAGINATION,
            current: offset / limit + 1,
            pageSize: limit,
            total: logsTotal,
            onChange: handleLogsPagination,
          }}
          emptyProps={{ noun: "logs" }}
        />
      </Card>

      <Modal
        title="Vital Information"
        open={isVitalModalOpen}
        onCancel={() => setIsVitalModalOpen(false)}
        footer={null}
        width={700}
      >
        <Table
          size="small"
          rowKey="key"
          pagination={false}
          columns={vitalColumns}
          dataSource={vitalInfoTableData}
        />
      </Modal>

      <Modal
        title="Sync Cabinet Slots"
        open={isSyncModalOpen}
        onCancel={() => setIsSyncModalOpen(false)}
        onOk={handleSyncSlots}
        okText="Sync"
      >
        <Typography.Text strong>Slots</Typography.Text>
        <Input
          type="number"
          placeholder="Enter number of slots"
          value={slots}
          onChange={(e) => setSlots(e.target.value)}
          min={1}
        />
      </Modal>
      <Modal
        title="OTA Update"
        open={isOtaModalOpen}
        onCancel={() => setIsOtaModalOpen(false)}
        onOk={confirmOtaUpdate}
        okText="Trigger OTA"
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong>Version</Typography.Text>
          <Input
            placeholder="Enter version (e.g. 1.2.19)"
            value={otaVersion}
            onChange={(e) => setOtaVersion(e.target.value)}
          />
        </div>

        <div>
          <Typography.Text strong>Build URL</Typography.Text>
          <Input
            placeholder="Enter build URL"
            value={otaBuildUrl}
            onChange={(e) => setOtaBuildUrl(e.target.value)}
          />
        </div>
      </Modal>
    </Spin>
  );
};

export default OverviewScreen;
